import React, { useState, useEffect, useRef } from 'react';
import { X, Check } from 'lucide-react';
import { FONT_FAMILIES, FONT_SIZES } from './constants';

const TextModal = ({ isOpen, onClose, onConfirm, darkMode, initialText = '', initialFontSize = 32, initialFontFamily = 'Inter' }) => {
    const [text, setText] = useState(initialText);
    const [fontSize, setFontSize] = useState(initialFontSize);
    const [fontFamily, setFontFamily] = useState(initialFontFamily);
    const inputRef = useRef(null);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
        if (isOpen) {
            setText(initialText);
            setFontSize(initialFontSize);
            setFontFamily(initialFontFamily);
        }
    }, [isOpen, initialText, initialFontSize, initialFontFamily]);

    const handleConfirm = () => {
        if (text.trim()) {
            onConfirm({ text: text.trim(), fontSize, fontFamily });
        }
        onClose();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleConfirm();
        }
        if (e.key === 'Escape') {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className={`
                w-full max-w-md mx-4 p-6 rounded-3xl shadow-2xl
                ${darkMode
                    ? 'bg-gray-800 border border-white/10'
                    : 'bg-white border border-gray-200'
                }
            `}>
                <h3 className={`text-lg font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                    Añadir Texto
                </h3>

                <textarea
                    ref={inputRef}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Escribe tu texto aquí..."
                    rows={3}
                    className={`
                        w-full px-4 py-3 rounded-2xl border-2 resize-none
                        transition-all duration-200 focus:outline-none
                        ${darkMode
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-violet-500'
                            : 'bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400 focus:border-blue-500'
                        }
                    `}
                    style={{ fontFamily }}
                />

                <div className="flex gap-4 mt-4">
                    <div className="flex-1">
                        <label className={`text-xs font-semibold uppercase mb-2 block ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Fuente
                        </label>
                        <select
                            value={fontFamily}
                            onChange={(e) => setFontFamily(e.target.value)}
                            className={`
                                w-full px-3 py-2 rounded-xl border-2 cursor-pointer
                                ${darkMode
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-gray-50 border-gray-200 text-gray-800'
                                }
                            `}
                        >
                            {FONT_FAMILIES.map(font => (
                                <option key={font} value={font} style={{ fontFamily: font }}>
                                    {font}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="w-24">
                        <label className={`text-xs font-semibold uppercase mb-2 block ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Tamaño
                        </label>
                        <select
                            value={fontSize}
                            onChange={(e) => setFontSize(parseInt(e.target.value))}
                            className={`
                                w-full px-3 py-2 rounded-xl border-2 cursor-pointer
                                ${darkMode
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-gray-50 border-gray-200 text-gray-800'
                                }
                            `}
                        >
                            {FONT_SIZES.map(size => (
                                <option key={size} value={size}>{size}px</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Preview */}
                <div className={`
                    mt-4 p-4 rounded-2xl min-h-[60px] flex items-center justify-center
                    ${darkMode ? 'bg-gray-700/50' : 'bg-gray-100'}
                `}>
                    <span
                        className={`${darkMode ? 'text-white' : 'text-gray-800'}`}
                        style={{ fontFamily, fontSize: Math.min(fontSize, 32) }}
                    >
                        {text || 'Vista previa...'}
                    </span>
                </div>

                <div className="flex gap-3 mt-6">
                    <button
                        onClick={onClose}
                        className={`
                            flex-1 py-3 rounded-2xl font-semibold flex items-center justify-center gap-2
                            transition-all duration-200
                            ${darkMode
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }
                        `}
                    >
                        <X size={18} />
                        Cancelar
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={!text.trim()}
                        className={`
                            flex-1 py-3 rounded-2xl font-semibold flex items-center justify-center gap-2
                            transition-all duration-200
                            ${text.trim()
                                ? darkMode
                                    ? 'bg-violet-600 text-white hover:bg-violet-500'
                                    : 'bg-blue-500 text-white hover:bg-blue-600'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }
                        `}
                    >
                        <Check size={18} />
                        Añadir
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TextModal;
